import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/state/AuthContext'
import { PublicAuthLayout } from '@/components/public/PublicAuthLayout'
import { ButtonLink, Notice } from '@/components/play'
import { authLink, safeReturnTo } from '@/components/onboarding/authContinuation'
import { OAuthEmailStep } from '@/components/onboarding/OAuthEmailStep'
import { clearIntent } from '@/lib/watch/intent'

export function OAuthCallbackPage() {
  const navigate = useNavigate()
  const { completeOAuthLogin } = useAuth()
  const handled = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [returnTo, setReturnTo] = useState('/app')
  // A first sign-in with a provider that shares no email address (Strava): its sealed identity, until an email link
  // finishes it.
  const [emailStep, setEmailStep] = useState<{ provider: string; pending: string } | null>(null)

  useEffect(() => {
    if (handled.current) return
    handled.current = true
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const next = safeReturnTo(params.get('return_to'))
    setReturnTo(next)
    const token = params.get('token')
    const pending = params.get('email_required')
    // Remove credentials from browser history even if authentication later fails.
    window.history.replaceState(window.history.state, '', window.location.pathname)
    if (token) {
      completeOAuthLogin(token)
      navigate(next, { replace: true })
    } else if (pending) {
      // A watch screen's intent stays: the email link signs in and returns to it.
      setEmailStep({ provider: params.get('provider') ?? '', pending })
    } else {
      // A watch screen's chat draft or go live waits for this sign-in; it failed, so the next one mustn't complete it.
      clearIntent()
      setError(params.get('error') || "We couldn't sign you in. Try again.")
    }
  }, [completeOAuthLogin, navigate])

  return (
    <PublicAuthLayout eyebrow="Sign in" title="Nearly there." description={emailStep ? 'One more step.' : "We're getting your events ready."} backLink={{ to: '/', label: 'StreamBridge' }}>
      {emailStep ? (
        <OAuthEmailStep provider={emailStep.provider} pending={emailStep.pending} returnTo={returnTo} />
      ) : error ? (
        <div className="space-y-5">
          <Notice tone="error" alert>{error}</Notice>
          <ButtonLink to={authLink('/login', returnTo)} className="w-full">Back to sign in</ButtonLink>
          <ButtonLink to={authLink('/signup', returnTo)} variant="ghost" className="w-full">Create an account with email</ButtonLink>
        </div>
      ) : <ContentSkeleton label="Signing you in" variant="list" count={1} />}
    </PublicAuthLayout>
  )
}
